/* import __COLOCATED_TEMPLATE__ from './credit-invoices-table.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { all, dropTask } from 'ember-concurrency';
import { PAGINATION_DEFAULT_COUNT_PER_PAGE } from 'sh-www/constants/routes/reports';
import { showModal, showNextModal } from 'sh-www/utils/modal-controls';
import { absoluteStoryhunterUrl } from 'sh-www/utils/url';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { BUTTON_TYPES } from 'sh-www/constants/lsg';
import { makeMoney } from 'sh-www/helpers/make-money';
import ConfirmMoneyIcon from 'sh-www/components/icons/confirm-money';
import clsx from 'clsx';

const CLASS_NAME = 'SH-c-reports-credit-invoices-table';
const ROUTE_CLASS_NAME = 'SH-r-reports-company-credits';
const EMPTY_CELL_TEXT = '—';

class ReportsCreditInvoicesTable extends Component {
  @service media;
  @service session;

  @tracked isOpen = Boolean(this.args.openByDefault);

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
      `${ROUTE_CLASS_NAME}__bubble-container`,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get routeClassName() {
    return ROUTE_CLASS_NAME;
  }

  get emptyCellText() {
    return EMPTY_CELL_TEXT;
  }

  get total() {
    return this.args.pagination?.total ?? 0;
  }

  get totalPages() {
    return this.args.pagination?.totalPages ?? 0;
  }

  get currentPage() {
    return this.args.pagination?.currentPage ?? 0;
  }

  get previousPage() {
    return this.currentPage - 1;
  }

  get nextPage() {
    return this.currentPage + 1;
  }

  get disablePreviousPage() {
    return this.currentPage <= 1;
  }

  get disableNextPage() {
    return this.currentPage >= this.totalPages;
  }

  get pageControlsDisplayString() {
    const { total, currentPage } = this;

    const displayPageMin = (currentPage - 1) * PAGINATION_DEFAULT_COUNT_PER_PAGE + 1;
    const displayPageMax = Math.min(currentPage * PAGINATION_DEFAULT_COUNT_PER_PAGE, total);

    return `Showing ${displayPageMin} - ${displayPageMax} of ${total}`;
  }

  get isShAdminViewing() {
    return this.session.currentUser.isAdmin;
  }

  @action
  toggleOpen() {
    this.isOpen = !this.isOpen;
  }

  @action
  onDeleteClicked(shGeneralInvoice) {
    showModal(this, 'reports/cancel-credit-modal', {
      shGeneralInvoice,
      loadCompanyCostCodeCreditsTask: this.args.loadCompanyCostCodeCreditsTask,
      loadCreditUnpaidInvoicesTask: this.args.loadCreditUnpaidInvoicesTask,
      loadCreditDeletedInvoicesTask: this.args.loadCreditDeletedInvoicesTask,
      loadCompanyCreditFactoidsTask: this.args.loadCompanyCreditFactoidsTask,
      updateCompanyCreditFactoids: this.args.updateCompanyCreditFactoids,
      updateCompanyCostCodeCredits: this.args.updateCompanyCostCodeCredits,
      updateShGeneralDeletedInvoices: this.args.updateShGeneralDeletedInvoices,
      updateShGeneralUnpaidInvoices: this.args.updateShGeneralUnpaidInvoices,
    });
  }

  @action
  onDownloadPDFClicked(shGeneralInvoice) {
    window.open(absoluteStoryhunterUrl(`/sh-general-invoices/${shGeneralInvoice.id}/pdf`));
  }

  @action
  onViewPaymentHistoryClicked(shGeneralInvoice) {
    showModal(this, 'reports/credit-payment-history-modal', {
      shGeneralInvoice,
      loadCompanyCostCodeCreditsTask: this.args.loadCompanyCostCodeCreditsTask,
      loadCreditUnpaidInvoicesTask: this.args.loadCreditUnpaidInvoicesTask,
      loadCreditDeletedInvoicesTask: this.args.loadCreditDeletedInvoicesTask,
      loadCompanyCreditFactoidsTask: this.args.loadCompanyCreditFactoidsTask,
      updateCompanyCreditFactoids: this.args.updateCompanyCreditFactoids,
      updateCompanyCostCodeCredits: this.args.updateCompanyCostCodeCredits,
      updateShGeneralDeletedInvoices: this.args.updateShGeneralDeletedInvoices,
      updateShGeneralUnpaidInvoices: this.args.updateShGeneralUnpaidInvoices,
    });
  }

  @action
  onRecordPaymentClicked(shGeneralInvoice) {
    showModal(this, 'reports/admin-record-payment-modal', {
      shGeneralInvoice,
      onComplete: ({ paymentAmount, paymentMethodValue }) =>
        this.onRecordPaymentComplete.perform({
          paymentAmount,
          paymentMethodValue,
        }),
    });
  }

  @dropTask
  *onRecordPaymentComplete({ paymentAmount, paymentMethodValue }) {
    const {
      loadCompanyCostCodeCreditsTask,
      loadCreditPaidInvoicesTask,
      loadCreditUnpaidInvoicesTask,
      loadCompanyCreditFactoidsTask,
      updateCompanyCreditFactoids,
      updateCompanyCostCodeCredits,
      updateShGeneralPaidInvoices,
      updateShGeneralUnpaidInvoices,
    } = this.args;

    // reload data
    try {
      const [
        companyCostCodeCredits,
        companyCreditFactoids,
        creditPaidInvoices,
        creditUnpaidInvoices,
      ] = yield all([
        loadCompanyCostCodeCreditsTask.perform(),
        loadCompanyCreditFactoidsTask.perform(),
        loadCreditPaidInvoicesTask.perform(),
        loadCreditUnpaidInvoicesTask.perform(),
      ]);

      // update data
      updateCompanyCreditFactoids(companyCreditFactoids);

      updateCompanyCostCodeCredits(
        companyCostCodeCredits.costCodes,
        companyCostCodeCredits.costCodesCurrentPage,
      );

      updateShGeneralPaidInvoices(
        creditPaidInvoices.shGeneralPaidInvoices,
        creditPaidInvoices.shGeneralPaidInvoicesPagination,
      );

      updateShGeneralUnpaidInvoices(
        creditUnpaidInvoices.shGeneralUnpaidInvoices,
        creditUnpaidInvoices.shGeneralUnpaidInvoicesPagination,
      );
    } catch (e) {
      handleErrorResponseWithModal(
        this,
        e,
        'Payment recorded but failed to reload data. Please refresh the page.',
      );
      return;
    }

    const amountUSD = makeMoney([paymentAmount]);

    // show confirm
    showNextModal(this, 'confirmation', {
      classNames: 'SH-c-reports-admin-record-payment-modal__success-modal',
      titleText: 'Payment recorded',
      bodyText: `A ${amountUSD} ${paymentMethodValue} payment has been recorded.`,
      actionButtonText: 'Got it',
      width: 'medium',
      actionButtonLsgButtonType: BUTTON_TYPES.PRIMARY_REGULAR,
      iconComponentPath: ConfirmMoneyIcon,
      isDismissible: false,
    });
  }
}

export default ReportsCreditInvoicesTable;
